import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw } from 'lucide-react';

/**
 * After a deploy the new version waits in the service worker; a plain refresh keeps the
 * old one. It's switched to straight away (one reload) unless something has been typed on
 * this screen, which may be a half-filled form: then a one-tap refresh is offered instead,
 * so versions never mix and nothing typed is lost.
 */
export function UpdatePrompt() {
  const { pathname } = useLocation();
  const typed = useRef(false);
  const waiting = useRef(false);
  const apply = useRef(() => {});
  const [applying, setApplying] = useState(false);

  // A new screen starts with nothing typed.
  useEffect(() => { typed.current = false; }, [pathname]);
  useEffect(() => {
    const onInput = () => { typed.current = true; };
    window.addEventListener('input', onInput, true);
    return () => window.removeEventListener('input', onInput, true);
  }, []);

  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW({
    onNeedRefresh() {
      waiting.current = true;
      if (!typed.current) apply.current();
    },
    onRegisteredSW(_url, reg) {
      if (!reg) return;
      // Look for a new version every 30 minutes while the app is open, and whenever it's
      // opened again (an installed app on a phone resumes rather than reloads).
      setInterval(() => void reg.update(), 30 * 60_000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        if (waiting.current && !typed.current) apply.current();
        else void reg.update();
      });
    },
  });
  apply.current = () => {
    setApplying(true);
    void updateServiceWorker(true);
  };

  if (!needRefresh || applying) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-[max(12px,env(safe-area-inset-top))] z-[70] flex justify-center px-4">
      <button
        type="button"
        onClick={() => apply.current()}
        className="animate-rise pointer-events-auto flex items-center gap-2.5 rounded-full bg-ink px-4 py-2.5 text-[14px] font-semibold text-bg shadow-float"
      >
        <RefreshCw className="size-4" /> Update ready — tap to refresh
      </button>
    </div>
  );
}
